// ink-component
import React from 'react';
import camelCase from 'lodash/camelCase';
import styled, { css, ThemeProvider } from 'styled-components';
import { IconTypes } from '../../helpers/constants';
import getGlobalProps from '../../helpers/constants/global-props';
import * as iconTypes from './components';
import { iconCore, iconSizes, iconPlacements, iconSpinning } from './styles';

// using span to preserve the inline nature of svg placements
/* @internal */
export const StyledIcon = styled.span`
  display: inline-flex;
  ${({ theme }) => iconPlacements[theme.iconPlacement]};

  svg {
    ${iconCore};

    ${({ theme }) =>
      theme.iconSpin &&
      css`
        ${iconSpinning}
      `}
  }
`;

// NOTE: regarding the placement prop, 'normal' is a value that doesn't map to anything. should be removed eventually
export interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** String identifier for the icon to be displayed.
   * The list of available icons is [here](/components/Icon/usage). */
  type?: IconTypes | string;
  /** Size modifier */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  /** Placement modifier. NOTE: 'normal' is a value that doesn't map to anything. should be removed eventually */
  placement?: 'normal' | 'top' | 'up' | 'low' | 'lower' | 'lowest' | 'xxlow';
  /** Modifier for spinning icons */
  isSpinner?: boolean;
  /** Simple description for screen readers */
  meaning?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Hide element from the accessibility tree when set to "true".
   * Useful when wrapped inside a [Button](/components/Button) or
   * [Dropdown](/components/Dropdown) with other text content */
  'aria-hidden'?: 'true' | 'false';
  /** Allows screen readers to describe the content */
  'aria-label'?: string;
  /**
   * Informs screen reader users whether
   * items in a table or grid are sorted in ascending or descending order */
  'aria-sort'?: 'ascending' | 'descending' | 'none';
}

const globalProps = getGlobalProps({});

/**
 * @purpose A convenient way to use iconography
 * @categories illustration
 */
export function Icon(props: IconProps) {
  const {
    type,
    size,
    placement,
    isSpinner,
    meaning,
    className,
    style,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'aria-sort': ariaSort,
    ...otherProps
  } = props;
  const title = meaning || type;

  const getAriaLabel = () => {
    if (props['aria-hidden'] === 'true') return undefined;

    if (ariaLabel) return ariaLabel;

    if (meaning) return meaning;

    if (ariaSort) return ariaSort;

    return `${title} icon`;
  };

  const svgProps = {
    'aria-hidden': props['aria-hidden'] === 'true' ? 'true' : undefined,
    'aria-label': getAriaLabel(),
    'data-testid': dataTestId,
    role: 'img',
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 22 22',
    width: size !== undefined ? iconSizes[size] : iconSizes.md,
    height: size !== undefined ? iconSizes[size] : iconSizes.md,
  };

  const iconTypesParsed = {
    ...iconTypes,
    '2FaN': iconTypes.twoFaN,
    '2Fa': iconTypes.twoFa,
  };

  const SVGIcon = iconTypesParsed[camelCase(type)] ? iconTypesParsed[camelCase(type)] : iconTypesParsed.box;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        iconSize: size,
        iconPlacement: placement,
        iconSpin: isSpinner,
      })}
    >
      <StyledIcon {...otherProps} {...globalProps}>
        <SVGIcon {...svgProps}>{title !== type && <title>{title}</title>}</SVGIcon>
      </StyledIcon>
    </ThemeProvider>
  );
}

Icon.defaultProps = {
  type: undefined,
  size: 'md',
  placement: 'normal',
  isSpinner: false,
  meaning: undefined,
  'data-testid': undefined,
  'aria-hidden': undefined,
  'aria-label': undefined,
};

Icon.displayName = 'Icon';

export default Icon;
